import { skipToken } from '@apollo/client'
import { ArrowRight, CircleX, Newspaper } from 'lucide-react'
import { useMemo } from 'react'
import { H3, H4, Muted } from '@/components/sv/typographies'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import { useSuspenseQuery } from '@/lib/hooks'
import { isPresent } from '@/lib/utils'
import { SEVALLA_CHANGELOG_QUERY } from '@/routes/home/changelog/query'
import type {
  SevallaChangelogQuery,
  SevallaChangelogQueryVariables,
} from '@/routes/home/changelog/query.types.gen'

const isChangelogAvailable =
  import.meta.env.VITE_GRAPHQL_SERVER_URI !== 'https://gql-dev.sevalla.com'

export const ChangelogCard = () => {
  const { data } = useSuspenseQuery<
    SevallaChangelogQuery,
    SevallaChangelogQueryVariables
  >(SEVALLA_CHANGELOG_QUERY, isChangelogAvailable ? undefined : skipToken)
  const changelogEntries = useMemo(() => {
    return data?.sevallaChangelog.data ?? []
  }, [data])

  return (
    <Card className="bg-secondary p-0 gap-0">
      <CardHeader className="px-5 py-3 flex flex-row items-center justify-between gap-0">
        <CardTitle>
          <H3 className="flex items-center gap-2 text-sm text-muted-foreground">
            <Newspaper className="size-4" /> Changelog
          </H3>
        </CardTitle>
        <a
          href="https://docs.sevalla.com/changelog"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
        >
          View all
          <ArrowRight className="size-3.5" />
        </a>
      </CardHeader>
      <CardContent className="p-0">
        <Card className="p-0 border-x-0 border-b-0 shadow-none">
          <CardContent className="p-4">
            {changelogEntries.length > 0 ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-2">
                {changelogEntries.map((entry) => (
                  <ChangelogEntry
                    key={entry?.id}
                    date={entry?.date ?? ''}
                    title={entry?.title ?? ''}
                    tags={(entry?.tags ?? []).filter(isPresent)}
                  />
                ))}
              </div>
            ) : (
              <Item variant="muted">
                <ItemMedia>
                  <CircleX className="fill-destructive stroke-muted" />
                </ItemMedia>
                <ItemContent>
                  <ItemTitle>Failed to load in changelog</ItemTitle>
                  <ItemDescription>
                    If you keep seeing this message, please contact support.
                  </ItemDescription>
                </ItemContent>
              </Item>
            )}
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  )
}

type ChangelogEntryProps = {
  date: string
  title: string
  tags: string[]
}

const ChangelogEntry = ({ date, title, tags }: ChangelogEntryProps) => {
  const href = useMemo(() => {
    const baseUrl = 'https://docs.sevalla.com/changelog'
    const titleSlug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
    return `${baseUrl}#${titleSlug}`
  }, [title])
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="group flex flex-col gap-4 p-4 rounded-md border cursor-pointer hover:bg-accent/15 hover:shadow-sm transition-all"
    >
      <H4 className="text-md transition-colors group-hover:text-orange-400">
        {title}
      </H4>
      <div className="mt-auto">
        <Muted className="text-xs">{date}</Muted>
        <div className="relative flex items-center overflow-hidden gap-2 mt-2">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
          <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-r from-transparent to-card" />
        </div>
      </div>
    </a>
  )
}
